import React, { useState, useEffect, useRef } from "react";
import { X, Upload, FileUp, Loader2, Tag, Folder, FileText, CheckCircle2 } from "lucide-react";

export default function UploadDocumentModal({
  isOpen,
  onClose,
  currentFolder,
  allFolders = [],
  onSubmit,
  loading
}) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [description, setDescription] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setTitle("");
      setFolderId(currentFolder ? currentFolder.id : "");
      setTagInput("");
      setTags([]);
      setDescription("");
      setIsDragging(false);
      setError("");
    }
  }, [isOpen, currentFolder]);

  if (!isOpen) return null;

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    if (selectedFile.size > 50 * 1024 * 1024) { // 50MB limit
      setError("File size exceeds 50MB limit.");
      return;
    }
    setError("");
    setFile(selectedFile);
    if (!title.trim()) {
      // Clean file name for default title
      const cleanName = selectedFile.name.replace(/\.[^/.]+$/, "");
      setTitle(cleanName);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = tagInput.trim().replace(/^#/, "");
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a file to upload.");
      return;
    }
    setError("");
    try {
      await onSubmit({
        file,
        title: title.trim() || file.name,
        folderId: folderId || null,
        tags,
        description: description.trim()
      });
      onClose();
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Failed to upload document.");
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content glass-card animate-slide-up" style={{ maxWidth: "560px" }}>
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div 
              style={{ 
                width: "36px", 
                height: "36px", 
                borderRadius: "8px", 
                backgroundColor: "var(--primary-light)", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                color: "var(--primary)"
              }}
            >
              <Upload size={20} />
            </div>
            <h3 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 700, color: "var(--text-primary)" }}>
              Upload Marketing Document
            </h3>
          </div>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ marginTop: "1.25rem" }}>
          {/* Dropzone Area */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: isDragging ? "2px dashed var(--primary)" : "2px dashed var(--border-color)",
              backgroundColor: isDragging ? "var(--primary-light)" : "var(--bg-input)",
              borderRadius: "10px",
              padding: "1.5rem 1rem",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              marginBottom: "1.25rem"
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />
            {file ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem" }}>
                <CheckCircle2 size={24} style={{ color: "var(--success)" }} />
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "0.95rem" }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                    {formatFileSize(file.size)} • Ready to upload
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", marginLeft: "0.5rem" }}
                  title="Remove file"
                >
                  <X size={18} />
                </button>
              </div>
            ) : (
              <div>
                <FileUp size={32} style={{ color: "var(--primary)", margin: "0 auto 0.5rem auto", display: "block" }} />
                <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "0.95rem" }}>
                  Drag & drop files here, or <span style={{ color: "var(--primary)", textDecoration: "underline" }}>browse</span>
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
                  Supports PDF, Pitch Decks (PPTX), Word/Excel, High-Res Images, Videos, ZIPs (up to 50MB)
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="docTitle">Document Title</label>
            <input
              id="docTitle"
              type="text"
              required
              placeholder="e.g. 2026 Enterprise Capabilities Deck"
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid-2" style={{ gap: "1rem", marginTop: "1rem" }}>
            <div className="form-group">
              <label className="form-label" htmlFor="targetFolder">Target Folder</label>
              <select
                id="targetFolder"
                className="form-input"
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
              >
                <option value="">📁 Root / Top Level</option>
                {allFolders.map((f) => (
                  <option key={f.id} value={f.id}>
                    📁 {f.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="tagInput">Tags / Keywords</label>
              <input
                id="tagInput"
                type="text"
                placeholder="Press Enter to add tag..."
                className="form-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
              />
            </div>
          </div>

          {tags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.5rem" }}>
              {tags.map((t) => (
                <span
                  key={t}
                  className="sku-badge"
                  style={{
                    fontSize: "0.75rem",
                    padding: "0.2rem 0.5rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.3rem"
                  }}
                >
                  #{t}
                  <X
                    size={12}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRemoveTag(t)}
                  />
                </span>
              ))}
            </div>
          )}

          <div className="form-group" style={{ marginTop: "1rem" }}>
            <label className="form-label" htmlFor="docDescription">Description / Notes (Optional)</label>
            <textarea
              id="docDescription"
              rows={2}
              placeholder="Add key context, target audience, or usage instructions..."
              className="form-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {error && <div className="alert alert-danger" style={{ marginTop: "1rem" }}>{error}</div>}

          <div className="modal-footer" style={{ marginTop: "1.75rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || !file}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={16} style={{ marginRight: "0.4rem" }} />
                  Uploading...
                </>
              ) : (
                "Upload Document"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
